import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import NormalDialPadView from "./NormalDialPadView";
import Button from "./Button";
import MaterialIcons from "../icons/MaterialIcons";
import User from "../models/User";
import TextView from "./TextView";
import PageNavigator from "../classes/PageNavigator";
import IconButton from "./IconButton";
import IconView from "./IconView";
import { BuildContext } from "../classes/BuildContext";

type _ContactsViewOnAddButtonPressed = (number: string) => void;

type _AddCallOnPressed = (d3eState: ContactsViewRefs) => void;

type _DialPadButtonOnPressed = (d3eState: ContactsViewRefs) => void;

export interface ContactsViewProps extends BaseUIProps {
  key?: string;
  reportingManager: User;
  onAddButtonPressed: _ContactsViewOnAddButtonPressed;
}
/// To store state data for ContactsView
class ContactsViewRefs {
  public addCall: AddCallState = new AddCallState();
  public dialPadButton: DialPadButtonState = new DialPadButtonState();
}

interface DialPadButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: ContactsViewRefs;
  _onDialPadButtonPressed?: _DialPadButtonOnPressed;
}

class DialPadButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _DialPadButtonWithState extends ObservableComponent<DialPadButtonWithStateProps> {
  dialPadButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DialPadButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get dialPadButton(): DialPadButtonState {
    return this.props.d3eState.dialPadButton;
  }
  public get d3eState(): ContactsViewRefs {
    return this.props.d3eState;
  }
  public get _onDialPadButtonPressed(): _DialPadButtonOnPressed {
    return this.props._onDialPadButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("dialPadButton", null, this.dialPadButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["dialPadButton", "dialPadButton.hover"], this.rebuild);
  }
  public dialPadButtonOnEnter(event): void {
    return this.dialPadButton.setHover(true);
  }
  public dialPadButtonOnExit(event): void {
    return this.dialPadButton.setHover(false);
  }
  public dispose(): void {
    this.dialPadButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: this.dialPadButton.hover
        ? new ui.BoxConstraints({ minHeight: 30 })
        : new ui.BoxConstraints({ minHeight: 30 }),
      child: Button({
        decoration: this.dialPadButton.hover
          ? new ui.BoxDecoration({
              color: cStyle.c18,
              border: ui.Border.all({ color: cStyle.c16, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(3.0),
            })
          : new ui.BoxDecoration({
              color: cStyle.c14,
              border: ui.Border.all({ color: cStyle.c16, width: 1.0 }),
              borderRadius: ui.BorderRadius.circular(3.0),
            }),
        padding: this.dialPadButton.hover
          ? ui.EdgeInsets.symmetric({
              horizontal: 0.0,
              vertical: 8.0,
              transitions: new Map(),
            })
          : ui.EdgeInsets.symmetric({
              horizontal: 0.0,
              vertical: 8.0,
              transitions: new Map(),
            }),
        disable: this.dialPadButton.disable,
        onPressed: () => {
          this._onDialPadButtonPressed(this.d3eState);
        },
        onFocusChange: (val) => {},
        states: ui.joinStates({ "data-c0": this.dialPadButton.hover }, {}),
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          crossAxisAlignment: ui.CrossAxisAlignment.center,
          children: [
            IconView({
              icon: MaterialIcons.call,
              size: 25,
              className: "x487",
              key: "0",
            }),
            TextView({
              data: "Add New Number",
              style: new ui.TextStyle({ fontSize: 16 }),
              className: "xf91",
              key: "1",
            }),
          ],
          className: "xab8 hc h",
        }),
        onEnter: (event) => {
          this.dialPadButtonOnEnter(event);
        },
        onExit: (event) => {
          this.dialPadButtonOnExit(event);
        },
      }),
      className: "x62d hc h",
      states: ui.joinStates({ "data-c0": this.dialPadButton.hover }, {}),
    });
  }
}
function DialPadButtonWithState(props: DialPadButtonWithStateProps) {
  return React.createElement(_DialPadButtonWithState, props);
}

interface AddCallWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: ContactsViewRefs;
  _onAddCallIconPressed?: _AddCallOnPressed;
}

class AddCallState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _AddCallWithState extends ObservableComponent<AddCallWithStateProps> {
  addCallFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddCallWithStateProps) {
    super(props);

    this.initState();
  }
  public get addCall(): AddCallState {
    return this.props.d3eState.addCall;
  }
  public get d3eState(): ContactsViewRefs {
    return this.props.d3eState;
  }
  public get _onAddCallIconPressed(): _AddCallOnPressed {
    return this.props._onAddCallIconPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("addCall", null, this.addCall);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["addCall", "addCall.hover"], this.rebuild);
  }
  public addCallOnEnter(event): void {
    return this.addCall.setHover(true);
  }
  public addCallOnExit(event): void {
    return this.addCall.setHover(false);
  }
  public dispose(): void {
    this.addCall.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: cStyle.tButtonPrimaryPaddingOn,
        decoration: cStyle.tButtonPrimaryDecorationOn,
        disable: this.addCall.disable,
        onPressed: () => {
          this._onAddCallIconPressed(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          children: [
            IconView({
              icon: MaterialIcons.call,
              color: cStyle.c14,
              size: 25,
              className: "x3e6",
              key: "0",
            }),
            TextView({
              data: "Add",
              style: new ui.TextStyle({ fontSize: 16 }),
              className: "xbdc",
              key: "1",
            }),
          ],
        }),
        onEnter: (event) => {
          this.addCallOnEnter(event);
        },
        onExit: (event) => {
          this.addCallOnExit(event);
        },
      }),
      className: "xe51",
    });
  }
}
function AddCallWithState(props: AddCallWithStateProps) {
  return React.createElement(_AddCallWithState, props);
}

class _ContactsViewState extends ObservableComponent<ContactsViewProps> {
  static defaultProps = { reportingManager: null, onAddButtonPressed: null };
  d3eState: ContactsViewRefs = new ContactsViewRefs();
  showDialPad: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ContactsViewProps) {
    super(props);

    this.initState();
  }
  public get reportingManager(): User {
    return this.props.reportingManager;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("reportingManager", this.props.reportingManager);

    this.on(
      [
        "reportingManager",
        "reportingManager.firstName",
        "reportingManager.lastName",
        "reportingManager.twilioNumber",
        "showDialPad",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ContactsViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.reportingManager !== this.props.reportingManager) {
      this.updateObservable(
        "reportingManager",
        prevProps.reportingManager,
        this.props.reportingManager
      );

      this.fire("reportingManager", this);
    }
  }
  public setShowDialPad(val: boolean): void {
    let isValChanged: boolean = this.showDialPad !== val;

    if (!isValChanged) {
      return;
    }

    this.showDialPad = val;

    this.fire("showDialPad", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisSize: ui.MainAxisSize.min,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            this.showDialPad
              ? IconButton({
                  icon: MaterialIcons.arrow_back,
                  size: 18,
                  onPressed: () => {
                    this.backButtonHandler(this.d3eState);
                  },
                })
              : [],
            TextView({
              data: "Add Conference",
              style: new ui.TextStyle({ fontSize: 20 }),
              className: "x1a3",
              key: "1",
            }),
            IconButton({
              icon: MaterialIcons.close,
              size: 18,
              onPressed: () => {
                this.onCancelButtonPressed(this.d3eState);
              },
              key: "2",
            }),
          ],
          className: "x3eb hc h",
          key: "0",
        }),
        !this.showDialPad &&
        this.reportingManager !== null &&
        this.reportingManager.twilioNumber !== null
          ? ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
              crossAxisAlignment: ui.CrossAxisAlignment.center,
              children: [
                ui.Row({
                  children: [
                    ui.Container({
                      child: ui.Container({
                        alignment: ui.Alignment.center,
                        child: TextView({
                          data:
                            this.reportingManager.firstName[0].toUpperCase() +
                            this.reportingManager.lastName[0].toUpperCase(),
                          textAlign: ui.TextAlign.center,
                          style: new ui.TextStyle({
                            color: new ui.Color(0xff000000),
                            fontWeight: ui.FontWeight.bold,
                          }),
                          className: "x353 hc vc",
                        }),
                        className: "x081 hc vc",
                      }),
                      key: "0",
                    }),
                    ui.Column({
                      mainAxisAlignment: ui.MainAxisAlignment.start,
                      crossAxisAlignment: ui.CrossAxisAlignment.start,
                      children: [
                        TextView({
                          data:
                            this.reportingManager.firstName +
                            " " +
                            this.reportingManager.lastName,
                          overflow: ui.TextOverflow.ellipsis,
                          style: new ui.TextStyle({ fontSize: 16 }),
                          className: "x864 hc",
                          key: "0",
                        }),
                        TextView({
                          data: "Report Manager",
                          className: "x26b",
                          key: "1",
                        }),
                      ],
                      className: "x8b2",
                      key: "1",
                    }),
                  ],
                  key: "0",
                }),
                AddCallWithState({
                  d3eState: this.d3eState,
                  _onAddCallIconPressed: this.onAddCallIconPressed,
                  key: "1",
                }),
              ],
              className: "xb53 hc h",
            })
          : [],
        !this.showDialPad &&
        this.reportingManager !== null &&
        this.reportingManager.twilioNumber !== null
          ? ui.Container({ className: "xa61 hc vc" })
          : [],
        !this.showDialPad &&
        this.reportingManager !== null &&
        this.reportingManager.twilioNumber !== null
          ? DialPadButtonWithState({
              d3eState: this.d3eState,
              _onDialPadButtonPressed: this.onDialPadButtonPressed,
            })
          : [],
        this.showDialPad ||
        this.reportingManager === null ||
        this.reportingManager.twilioNumber === null
          ? NormalDialPadView({
              onCallConnect: (number) => {
                this.onDialPadView(number, this.d3eState);
              },
              className: "xa50 hc h",
            })
          : [],
      ],
      className: ui.join("ContactsView xa9e hc vc ", this.props.className),
      ...copyBaseUIProps(this.props),
    });
  }
  public onCancelButtonPressed = (d3eState: ContactsViewRefs): void => {
    this.navigator.close();
  };
  public onDialPadView = (
    numberValue: string,
    d3eState: ContactsViewRefs
  ): void => {
    this.onAddButtonPressed(numberValue);
  };
  public onDialPadButtonPressed = (d3eState: ContactsViewRefs): void => {
    this.setShowDialPad(!this.showDialPad);
  };
  public backButtonHandler = (d3eState: ContactsViewRefs): void => {
    this.setShowDialPad(false);
  };
  public onAddCallIconPressed = (d3eState: ContactsViewRefs): void => {
    this.onAddButtonPressed(this.reportingManager.twilioNumber);
  };
  public get onAddButtonPressed(): _ContactsViewOnAddButtonPressed {
    return this.props.onAddButtonPressed;
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get addCall() {
    return this.d3eState.addCall;
  }
  public get dialPadButton() {
    return this.d3eState.dialPadButton;
  }
}
export default function ContactsView(props: ContactsViewProps) {
  return React.createElement(_ContactsViewState, {
    ..._ContactsViewState.defaultProps,
    ...props,
  });
}
